'use strict';
/* ==========================================================================
   levels/title.js — the title screen of "The Ground Is Moving".
   Paper collage (DESIGN.md §2): an old survey sheet with contour lines, a torn-paper strata band along the bottom
   (limestone … basalt, a thread of magma, the mantle), Dr. Quinn standing on it, the lesson title in ransom letters
   (from lesson.json), and a field card with the REAL map: every M ≥ 5 earthquake of 2025 over Natural Earth coasts
   (core-local/look.js, assets/data/). A big ▶ Play → the map. On a phone held upright the controls move to the portrait dock (Dock.title) and the
   stage keeps only the art. Levels.title.loading() draws the "cutting paper…" screen while assets load (no
   textures exist yet then: plain fills + ink only).
   Conventions (shared/engine/v2/README.md): draw through g, a pure function of time (T, this.enteredAt), sizes of
   tappable things through UI.button / UI.tap, no per-frame shadowOn().
   ========================================================================== */
(function () {
  const PLAY = { x: 1580, y: 720 };

  /* the title in ransom letters: up to two lines, sized to fit 1000 logical px */
  function titleLines(text) {
    const words = String(text).toUpperCase().split(/\s+/).filter(Boolean);
    if (words.length < 3 && text.length < 14) return [words.join(' ')];
    let best = null;
    for (let k = 1; k < words.length; k++) {
      const a = words.slice(0, k).join(' '), b = words.slice(k).join(' '), d = Math.abs(a.length - b.length);
      if (!best || d < best.d) best = { d, lines: [a, b] };
    }
    return best ? best.lines : [text.toUpperCase()];
  }
  const fitSize = (line, seed, maxW, max) => { const w = ransom(line, -5000, -5000, 100, 1e9, { seed }); return Math.min(max, (maxW / Math.max(1, w)) * 100); };

  function backdrop(t0) {
    g.fillStyle = paperTile(GroundLook.COL.survey, 5); g.fillRect(0, 0, Game.W, Game.H);
    GroundLook.contours(960, 440, 1920, 880, 3, 0.9);
    // the field card: a year of real earthquakes (the lesson's hook: the quakes draw the plate edges)
    const cs = pop(T, t0 + 0.9, 0.45);
    if (cs > 0) {
      g.save(); g.translate(1060, 725); g.scale(cs, cs);
      GroundLook.quakeMap(0, 0, 480, 206, { seed: 21, rot: -0.02, depth: 1.4 });
      hand('every M5+ quake of 2025', 60, 128, 28, t0 + 1.3, { color: PAL.navy, rot: -0.02, seed: 23 });
      g.restore();
      if (cs >= 1) { tape(1060 - 214, 725 - 96, 110, -0.5, undefined, 8); tape(1060 + 222, 725 - 102, 100, 0.45, undefined, 9); }
    }
    GroundLook.strata(895, 200, { seed: 4 });
    paperCircle(1810, 110, 70, PAL.mustard, { seed: 8, depth: 0.8 });
    tape(900, 60, 170, -0.2, undefined, 2);
    doodleStar(840, 300, 24, { seed: 3, rot: T * 0.4, s: pop(T, t0 + 1.2) });
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a paper tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      backdrop(t0);
      // Dr. Quinn rises in from below and stands on the strata
      const rise = E.outBack(P(T, t0 + 0.5, t0 + 1.0), 1.4);
      Puppet.draw('quinn', 470, 930 + (1 - rise) * 700, 780, { facing: 1, t: T });
      // title (ransom) + subtitle (hand)
      const lines = titleLines(L.title || 'NEW LESSON');
      const sizes = lines.map((l, i) => fitSize(l, 101 + i, 1000, lines.length > 1 ? 118 : 140));
      let y = lines.length > 1 ? 170 : 230;
      lines.forEach((l, i) => { ransom(l, 1330, y, sizes[i], t0 + 0.4 + i * 0.4, { seed: 101 + i }); y += sizes[i] * 1.35; });
      hand(L.subtitle || '', 1360, y + 40, 72, t0 + 1.2, { dur: 0.5, color: PAL.navy, rot: -0.03 });
      ink([[1100, y + 90], [1330, y + 83], [1580, y + 91]], { w: 6, seed: 19, prog: P(T, t0 + 1.6, t0 + 1.9), color: PAL.red });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 380, h: 130, color: PAL.red, font: "'Luckiest Guy'", size: 66, rot: -0.02 })) this.go();
      if (t > 2.4) hand(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 110 + (UI.tap(136) - 136) / 2, UI.text(38), t0 + 2.4, { color: 'rgba(43,36,32,0.7)', seed: 61 });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* loading screen: no textures yet → plain paper colour, ink scissors snipping along a dashed line */
  Title.loading = function () {
    g.fillStyle = PAL.paper; g.fillRect(0, 0, Game.W, Game.H);
    const cx = 960, cy = 470, x0 = 620, x1 = 1300;
    const prog = (T * 0.35) % 1, sx = lerp(x0, x1, prog);
    for (let x = x0; x < x1; x += 34) ink([[x, cy + 60], [x + 18, cy + 60]], { w: 4, seed: x, alpha: x < sx ? 0.25 : 0.8, passes: 1, step: 20 });
    const open = 0.28 + Math.sin(T * 12) * 0.22;
    g.save(); g.translate(sx, cy + 60);
    [-1, 1].forEach(side => {
      g.save(); g.rotate(side * open);
      ink([[0, 0], [120, -6 * side], [150, 0]], { w: 7, seed: 70 + side, color: PAL.ink, step: 12 });
      ink([[0, 0], [-60, 18 * side]], { w: 7, seed: 72 + side, step: 12 });
      inkCircle(-86, 28 * side, 26, { w: 6, seed: 74 + side, color: side > 0 ? PAL.red : PAL.navy });
      g.restore();
    });
    g.restore();
    hand('cutting paper…', cx, cy + 190, 84, 0, { dur: 0.01, seed: 5 });
  };

  Levels.title = Title;
})();
